import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import TransactionEditModal from './TransactionEditModal';
import { bankingService } from '../../../services/bankingService';

const TransactionTable = ({ transactions, onCategorize, onReconcile, onBulkAction, onTransactionUpdate }) => {
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Load enhanced category options on component mount
  useEffect(() => {
    loadCategoryOptions();
  }, []);

  const loadCategoryOptions = async () => {
    try {
      setLoadingCategories(true);
      const options = await bankingService?.getEnhancedCategoryOptions();
      setCategoryOptions(options || []);
    } catch (error) {
      console.error('Error loading category options:', error);
      // Set fallback basic categories
      setCategoryOptions([
        { value: '', label: 'Select Category', isHeader: false },
        { value: 'Revenue', label: 'Revenue', isHeader: false },
        { value: 'Office Supplies', label: 'Office Supplies', isHeader: false },
        { value: 'Other', label: 'Other', isHeader: false }
      ]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(Math.abs(amount));
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTransactionTypeIcon = (amount) => {
    return amount > 0 ? 'ArrowDownLeft' : 'ArrowUpRight';
  };

  const getTransactionTypeColor = (amount) => {
    return amount > 0 ? 'text-success' : 'text-error';
  };

  const getReconciliationStatus = (status) => {
    switch (status) {
      case 'reconciled':
        return { color: 'text-success bg-success/10', icon: 'CheckCircle', text: 'Reconciled' };
      case 'pending':
        return { color: 'text-warning bg-warning/10', icon: 'Clock', text: 'Pending' };
      case 'unreconciled':
        return { color: 'text-error bg-error/10', icon: 'AlertCircle', text: 'Unreconciled' };
      default:
        return { color: 'text-muted-foreground bg-muted/10', icon: 'Circle', text: 'Unknown' };
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectTransaction = (transactionId) => {
    setSelectedTransactions(prev => 
      prev?.includes(transactionId)
        ? prev?.filter(id => id !== transactionId)
        : [...prev, transactionId]
    );
  };

  const handleSelectAll = () => {
    setSelectedTransactions(prev => 
      prev?.length === transactions?.length ? [] : transactions?.map(t => t?.id)
    );
  };

  const handleCategoryChange = (transactionId, newCategory) => {
    onCategorize(transactionId, newCategory);
    setEditingCategory(null);
  };

  const handleCancelCategoryEdit = () => {
    setEditingCategory(null);
  };

  const handleMoreActionClick = (transactionId, event) => {
    event?.stopPropagation();
    setActiveDropdown(activeDropdown === transactionId ? null : transactionId);
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setShowEditModal(true);
    setActiveDropdown(null);
  };

  const handleEditSave = (updatedTransaction) => {
    // Call parent callback to refresh transactions
    if (onTransactionUpdate) {
      onTransactionUpdate(updatedTransaction);
    }
    setEditingTransaction(null);
    setShowEditModal(false);
  };

  const handleEditClose = () => {
    setEditingTransaction(null);
    setShowEditModal(false);
  };

  const handleDeleteTransaction = async (transactionId) => {
    try {
      await bankingService?.deleteTransaction(transactionId);
      // Refresh transactions by calling parent component's refresh method
      if (onTransactionUpdate) {
        onTransactionUpdate(null, 'delete', transactionId);
      } else {
        window.location?.reload(); // Fallback refresh
      }
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert('Failed to delete transaction: ' + error?.message);
    } finally {
      setShowDeleteConfirmation(null);
      setActiveDropdown(null);
    }
  };

  const handleDuplicateTransaction = async (transaction) => {
    try {
      const duplicatedData = {
        bankAccountId: transaction?.bankAccountId,
        transactionDate: new Date()?.toISOString()?.split('T')?.[0], // Today's date
        description: `${transaction?.description} (Copy)`,
        amount: transaction?.amount,
        category: transaction?.category,
        reconciliationStatus: 'unreconciled'
      };
      
      const newTransaction = await bankingService?.createTransaction(duplicatedData);
      // Refresh transactions
      if (onTransactionUpdate) {
        onTransactionUpdate(newTransaction, 'create');
      } else {
        window.location?.reload(); // Fallback refresh
      }
    } catch (error) {
      console.error('Error duplicating transaction:', error);
      alert('Failed to duplicate transaction: ' + error?.message);
    } finally {
      setActiveDropdown(null);
    }
  };

  const handleSplitTransaction = (transaction) => {
    // For now, show an alert that this feature is coming soon
    alert('Split Transaction feature coming soon!');
    setActiveDropdown(null);
  };

  const handleViewTransactionDetails = (transaction) => {
    // For now, show transaction details in an alert
    const details = `Transaction Details:
    
Date: ${formatDate(transaction?.transactionDate)}
Description: ${transaction?.description}
Amount: ${formatCurrency(transaction?.amount)}
Category: ${transaction?.category || 'Uncategorized'}
Status: ${transaction?.reconciliationStatus}
Reference: ${transaction?.referenceNumber || 'N/A'}
Created: ${new Date(transaction?.createdAt)?.toLocaleString()}`;
    
    alert(details);
    setActiveDropdown(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActiveDropdown(null);
    };
    
    if (activeDropdown) {
      document?.addEventListener('click', handleClickOutside);
      return () => document?.removeEventListener('click', handleClickOutside);
    }
  }, [activeDropdown]);

  const getCategoryDisplay = (categoryKey) => {
    if (!categoryKey) {
      return { label: 'Uncategorized', icon: '❓', color: 'text-gray-500' };
    }

    // First, try to find it in the loaded category options
    const categoryOption = categoryOptions?.find(opt => 
      opt?.value === categoryKey && !opt?.isHeader
    );

    if (categoryOption) {
      // If it's an account-based category, show with account type info
      if (categoryOption?.accountType) {
        return {
          label: categoryOption?.label,
          icon: getAccountTypeIcon(categoryOption?.accountType),
          color: getAccountTypeColor(categoryOption?.accountType),
          isAccountBased: true,
          accountType: categoryOption?.accountType,
          accountCode: categoryOption?.accountCode
        };
      }
      
      // Static category
      return {
        label: categoryOption?.label,
        icon: getStaticCategoryIcon(categoryKey),
        color: 'text-gray-600'
      };
    }

    // Fallback for categories not in the options (legacy data)
    return {
      label: categoryKey,
      icon: getStaticCategoryIcon(categoryKey),
      color: 'text-gray-500'
    };
  };

  // Helper function to get account type icons
  const getAccountTypeIcon = (accountType) => {
    const icons = {
      'asset': '💰',
      'liability': '📋', 
      'equity': '🏛️',
      'revenue': '📈',
      'expense': '📉'
    };
    return icons?.[accountType] || '📁';
  };

  // Helper function to get account type colors
  const getAccountTypeColor = (accountType) => {
    const colors = {
      'asset': 'text-green-600',
      'liability': 'text-red-600',
      'equity': 'text-blue-600', 
      'revenue': 'text-emerald-600',
      'expense': 'text-orange-600'
    };
    return colors?.[accountType] || 'text-gray-600';
  };

  // Helper function to get static category icons
  const getStaticCategoryIcon = (category) => {
    const icons = {
      'customer_payment': '👤',
      'customer_advance': '💰',
      'vendor_payment': '🏢',
      'vendor_advance': '💳', 
      'transfer_to_bank': '🔄',
      'Office Supplies': '📎',
      'Marketing & Advertising': '📢',
      'Travel & Entertainment': '✈️',
      'Utilities': '💡',
      'Professional Services': '👔',
      'Equipment & Software': '🔧',
      'Rent & Facilities': '🏢',
      'Bank Fees': '🏦',
      'Revenue': '💰',
      'Other': '❓'
    };
    return icons?.[category] || '📋';
  };

  const getAIConfidenceDisplay = (confidence) => {
    if (!confidence) return null;
    
    const percentage = Math.round(confidence * 100);
    let color = 'text-muted-foreground';
    let label = 'Low';
    
    if (percentage >= 80) {
      color = 'text-success';
      label = 'High';
    } else if (percentage >= 60) {
      color = 'text-warning';
      label = 'Medium';
    }
    
    return { percentage, color, label };
  };

  // Custom Select component that handles hierarchical options
  const renderCategorySelect = (value, onChange, disabled = false) => {
    return (
      <select 
        value={value || ''}
        onChange={(e) => onChange(e?.target?.value)}
        disabled={disabled || loadingCategories}
        className="w-full p-2 border border-border rounded bg-background text-foreground text-sm"
      >
        {categoryOptions?.map((option, index) => {
          if (option?.isHeader) {
            return (
              <optgroup key={`header-${index}`} label={option?.label?.replace(/──/g, '')?.trim()}>
              </optgroup>
            );
          }
          
          return (
            <option key={option?.value || index} value={option?.value}>
              {option?.label}
            </option>
          );
        })}
      </select>
    );
  };

  // Render the enhanced action dropdown
  const renderActionDropdown = (transaction) => {
    if (activeDropdown !== transaction?.id) return null;

    return (
      <div className="absolute right-0 top-8 bg-card border border-border rounded-lg shadow-lg py-2 min-w-[160px] z-50">
        <button
          onClick={() => handleViewTransactionDetails(transaction)}
          className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-foreground hover:bg-muted/20"
        >
          <Icon name="Eye" size={14} />
          <span>View Details</span>
        </button>
        <button
          onClick={() => handleEditTransaction(transaction)}
          className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-foreground hover:bg-muted/20"
        >
          <Icon name="Edit" size={14} />
          <span>Edit Transaction</span>
        </button>
        <button
          onClick={() => handleDuplicateTransaction(transaction)}
          className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-foreground hover:bg-muted/20"
        >
          <Icon name="Copy" size={14} />
          <span>Duplicate</span>
        </button>
        <button
          onClick={() => handleSplitTransaction(transaction)}
          className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-foreground hover:bg-muted/20"
        >
          <Icon name="Split" size={14} />
          <span>Split Transaction</span>
        </button>
        <hr className="my-2 border-border" />
        <button
          onClick={() => {
            setShowDeleteConfirmation(transaction?.id);
            setActiveDropdown(null);
          }}
          className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-error hover:bg-error/10"
        >
          <Icon name="Trash2" size={14} />
          <span>Delete</span>
        </button>
      </div>
    );
  };

  return (
    <>
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {/* Table Header with Bulk Actions */}
        {selectedTransactions?.length > 0 && (
          <div className="bg-primary/5 border-b border-border p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">
                {selectedTransactions?.length} transaction{selectedTransactions?.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onBulkAction('categorize', selectedTransactions)}
                  iconName="Tag"
                  iconPosition="left"
                >
                  Bulk Categorize
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onBulkAction('reconcile', selectedTransactions)}
                  iconName="CheckCircle"
                  iconPosition="left"
                >
                  Bulk Reconcile
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onBulkAction('auto-categorize', selectedTransactions)}
                  iconName="Sparkles"
                  iconPosition="left"
                >
                  AI Categorize
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedTransactions([])}
                  iconName="X"
                >
                  Clear
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30 border-b border-border">
              <tr>
                <th className="w-12 p-4">
                  <input
                    type="checkbox"
                    checked={selectedTransactions?.length === transactions?.length && transactions?.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-border"
                  />
                </th>
                <th className="text-left p-4">
                  <button
                    onClick={() => handleSort('date')}
                    className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                  >
                    <span>Date</span>
                    <Icon name="ArrowUpDown" size={14} />
                  </button>
                </th>
                <th className="text-left p-4">
                  <span className="text-sm font-medium text-foreground">Description</span>
                </th>
                <th className="text-left p-4">
                  <button
                    onClick={() => handleSort('amount')}
                    className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                  >
                    <span>Amount</span>
                    <Icon name="ArrowUpDown" size={14} />
                  </button>
                </th>
                <th className="text-left p-4">
                  <span className="text-sm font-medium text-foreground">Category</span>
                </th>
                <th className="text-left p-4">
                  <span className="text-sm font-medium text-foreground">Status</span>
                </th>
                <th className="text-left p-4">
                  <span className="text-sm font-medium text-foreground">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {transactions?.map((transaction) => {
                const reconciliationStatus = getReconciliationStatus(transaction?.reconciliationStatus);
                const isEditingThisCategory = editingCategory === transaction?.id;
                const categoryDisplay = getCategoryDisplay(transaction?.category);
                const confidenceDisplay = getAIConfidenceDisplay(transaction?.categoryConfidence);
                
                return (
                  <tr
                    key={transaction?.id}
                    className={`border-b border-border hover:bg-muted/20 transition-colors duration-150 ${
                      selectedTransactions?.includes(transaction?.id) ? 'bg-primary/5' : ''
                    }`}
                  >
                    <td className="p-4">
                      <input
                        type="checkbox"
                        checked={selectedTransactions?.includes(transaction?.id)}
                        onChange={() => handleSelectTransaction(transaction?.id)}
                        className="rounded border-border"
                      />
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-foreground">
                        {formatDate(transaction?.transactionDate)}
                      </span>
                    </td>
                    <td className="p-4">
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {transaction?.description}
                        </p>
                        {transaction?.referenceNumber && (
                          <p className="text-xs text-muted-foreground">
                            Ref: {transaction?.referenceNumber}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <Icon
                          name={getTransactionTypeIcon(transaction?.amount)}
                          size={16}
                          className={getTransactionTypeColor(transaction?.amount)}
                        />
                        <span className={`text-sm font-medium ${getTransactionTypeColor(transaction?.amount)}`}>
                          {transaction?.amount > 0 ? '+' : '-'}{formatCurrency(transaction?.amount)}
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      {isEditingThisCategory ? (
                        <div className="flex items-center space-x-2">
                          {renderCategorySelect(
                            transaction?.category || "",
                            (value) => handleCategoryChange(transaction?.id, value)
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancelCategoryEdit}
                            iconName="X"
                            className="h-8 w-8 p-0"
                          />
                        </div>
                      ) : transaction?.category ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-lg">{categoryDisplay?.icon}</span>
                            <div>
                              <span className={`text-sm font-medium ${categoryDisplay?.color}`}>
                                {categoryDisplay?.label}
                              </span>
                              {/* Show account type for account-based categories */}
                              {categoryDisplay?.isAccountBased && (
                                <div className="flex items-center space-x-1 mt-1">
                                  <span className="text-xs px-2 py-0.5 bg-muted rounded text-muted-foreground">
                                    {categoryDisplay?.accountType?.replace('_', ' ')}
                                  </span>
                                </div>
                              )}
                              {/* AI Confidence Indicator */}
                              {confidenceDisplay && (
                                <div className="flex items-center space-x-1 mt-1">
                                  <Icon name="Brain" size={10} className={confidenceDisplay?.color} />
                                  <span className={`text-xs ${confidenceDisplay?.color}`}>
                                    {confidenceDisplay?.percentage}% confidence
                                  </span>
                                </div>
                              )}
                            </div>
                            {/* AI Suggested Badge */}
                            {transaction?.aiSuggested && (
                              <div className="flex items-center space-x-1 px-2 py-1 bg-primary/10 rounded-full">
                                <Icon name="Sparkles" size={12} className="text-primary" />
                                <span className="text-xs text-primary">AI</span>
                              </div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingCategory(transaction?.id)}
                            iconName="Edit"
                            className="h-6 w-6 p-0 ml-2 opacity-60 hover:opacity-100"
                            title="Change category"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          {renderCategorySelect(
                            "",
                            (value) => onCategorize(transaction?.id, value)
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              // Trigger AI categorization for this transaction
                              onBulkAction('auto-categorize', [transaction?.id]);
                            }}
                            iconName="Sparkles"
                            className="h-8 w-8 p-0"
                            title="AI Categorize"
                          />
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${reconciliationStatus?.color}`}>
                        <Icon name={reconciliationStatus?.icon} size={12} />
                        <span>{reconciliationStatus?.text}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-1 relative">
                        {transaction?.reconciliationStatus !== 'reconciled' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onReconcile(transaction?.id)}
                            iconName="CheckCircle"
                            title="Mark as reconciled"
                          />
                        )}
                        <div className="relative">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="MoreHorizontal"
                            title="More options"
                            onClick={(e) => handleMoreActionClick(transaction?.id, e)}
                          />
                          {renderActionDropdown(transaction)}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {transactions?.length === 0 && (
          <div className="p-12 text-center">
            <Icon name="CreditCard" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No transactions found</h3>
            <p className="text-muted-foreground">
              Try adjusting your filters or sync your bank accounts to import transactions.
            </p>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirmation && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
                  <Icon name="Trash2" size={20} className="text-error" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Delete Transaction</h3>
                  <p className="text-sm text-muted-foreground">This action cannot be undone</p>
                </div>
              </div>
              
              <p className="text-sm text-foreground mb-6">
                Are you sure you want to delete this transaction? This will permanently remove it from your records.
              </p>
              
              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirmation(null)}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={() => handleDeleteTransaction(showDeleteConfirmation)}
                  className="bg-error text-white hover:bg-error/90"
                >
                  Delete Transaction
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Transaction Edit Modal */}
      <TransactionEditModal
        transaction={editingTransaction}
        isOpen={showEditModal}
        onClose={handleEditClose}
        onSave={handleEditSave}
      />
    </>
  );
};

export default TransactionTable;